// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Changes
    categoryContainer?: InjectableCss;
    categoryHeader?: InjectableCss;
    viewMoreLinkWrapper?: InjectableCss;
    viewMoreLink?: LinkPresentationProps;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.innerContainer}>
            <StyledWrapper {...styles.categoryContainer}>
                <StyledWrapper
                    {...styles.categoryHeader}
                    className={category.subCategoryIds?.length ? "has-subcategories" : ""}
                >
                    {showImages && category.smallImagePath && (
                        <Clickable
                            href={category.path}
                            {...styles.subCategoryImageClickable}
                            aria-label={category.shortDescription}
                        >
                            <LazyImage
                                width="108"
                                height="108"
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                                customTransformations={{ default: "GUEST_Product_110x110" }}
                            />
                        </Clickable>
                    )}
                    <Link
                        href={category.path}
                        {...styles.subCategoryNameLink}
                        data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                        aria-label={category.shortDescription}
                    >
                        {category.shortDescription}
                    </Link>
                </StyledWrapper>
                {createWidgetElement("Category/CategoryDetailLinkList", {
                    fields: {},
                    ...{ categoryProp: category, maxNumberOfCategories: 5 },
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.viewMoreLinkWrapper}>
                <Link href={category.path} {...styles.viewMoreLink} aria-label="View More">
                    {translate("View All")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const category = getCategoryState(state, ownProps.categoryId).value;
    const subCategoryIds = category ? getSubCategoryIds(state, category.id) : undefined;
    return {
        category: { ...category, subCategoryIds: category?.subCategoryIds || subCategoryIds },
    };
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
